import { useEffect, useRef, useCallback } from 'react';
import { useAuth, useAuthActions } from '@/stores/auth';
import {
  TOKEN_EXPIRY_STORAGE_KEY,
  TOKEN_REFRESH_THRESHOLD_MS,
  TOKEN_CHECK_INTERVAL_MS,
  LAST_ACTIVITY_STORAGE_KEY,
  INACTIVITY_TIMEOUT_MS,
} from '@/utils/constants';

/**
 * Hook that proactively refreshes tokens when:
 * 1. Token is nearing expiration (within threshold)
 * 2. User has been active (not already timed out)
 *
 * This ensures active users stay logged in indefinitely
 * while inactive users are logged out after the inactivity timeout.
 *
 * The hook:
 * - Checks token expiry every TOKEN_CHECK_INTERVAL_MS (60 seconds)
 * - Triggers refresh when token has < TOKEN_REFRESH_THRESHOLD_MS (5 min) remaining
 * - Only refreshes if user has been active within INACTIVITY_TIMEOUT_MS (35 min)
 * - Also triggers check on user activity (debounced)
 */
export function useProactiveTokenRefresh(enabled: boolean = true): void {
  const { isAuthenticated } = useAuth();
  const { refreshToken } = useAuthActions();
  const refreshingRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activityDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Check if token should be refreshed based on expiry time
   */
  const shouldRefreshToken = useCallback((): boolean => {
    const expiryStr = localStorage.getItem(TOKEN_EXPIRY_STORAGE_KEY);
    if (!expiryStr) {
      // No expiry stored - can't determine if refresh needed
      return false;
    }

    const expiryTime = parseInt(expiryStr, 10);
    if (isNaN(expiryTime)) {
      return false;
    }

    const now = Date.now();
    const timeUntilExpiry = expiryTime - now;

    // Refresh if token expires within threshold and hasn't expired yet
    return timeUntilExpiry > 0 && timeUntilExpiry <= TOKEN_REFRESH_THRESHOLD_MS;
  }, []);

  /**
   * Check if user has been active recently (not timed out)
   */
  const isUserActive = useCallback((): boolean => {
    const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
    if (!lastActivityStr) {
      // No activity recorded - assume active for new sessions
      return true;
    }

    const lastActivity = parseInt(lastActivityStr, 10);
    if (isNaN(lastActivity)) {
      return true;
    }

    const now = Date.now();
    const inactivityDuration = now - lastActivity;

    // Consider user active if activity within inactivity timeout
    return inactivityDuration < INACTIVITY_TIMEOUT_MS;
  }, []);

  /**
   * Attempt to refresh the token if conditions are met
   */
  const attemptRefresh = useCallback(async () => {
    // Prevent concurrent refresh attempts
    if (refreshingRef.current) {
      return;
    }

    // Check if refresh is needed
    if (!shouldRefreshToken()) {
      return;
    }

    // Check if user is still active
    if (!isUserActive()) {
      // User is inactive - don't refresh, they'll be logged out by inactivity handler
      return;
    }

    refreshingRef.current = true;

    try {
      await refreshToken();
      // console.debug('Proactive token refresh successful');
    } catch (error) {
      console.error('Proactive token refresh failed:', error);
      // Don't logout here - let the normal 401 interceptor handle it
      // or the inactivity handler will take care of it
    } finally {
      refreshingRef.current = false;
    }
  }, [shouldRefreshToken, isUserActive, refreshToken]);

  /**
   * Set up periodic token check interval
   */
  useEffect(() => {
    if (!enabled || !isAuthenticated) {
      // Clean up if disabled or not authenticated
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Check immediately on mount
    void attemptRefresh();

    // Set up periodic check
    intervalRef.current = setInterval(() => {
      void attemptRefresh();
    }, TOKEN_CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, isAuthenticated, attemptRefresh]);

  /**
   * Also trigger check when user shows activity
   * This ensures we don't miss refresh opportunities when user becomes active
   */
  useEffect(() => {
    if (!enabled || !isAuthenticated) {
      return;
    }

    const handleActivity = () => {
      // Debounce activity-triggered checks to avoid excessive refreshes
      if (activityDebounceRef.current) {
        clearTimeout(activityDebounceRef.current);
      }

      activityDebounceRef.current = setTimeout(() => {
        void attemptRefresh();
      }, 2000); // 2 second debounce for activity-triggered refresh
    };

    // Listen for user activity events
    const activityEvents = ['mousemove', 'keydown', 'click', 'touchstart'];
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (activityDebounceRef.current) {
        clearTimeout(activityDebounceRef.current);
      }
    };
  }, [enabled, isAuthenticated, attemptRefresh]);
}

export default useProactiveTokenRefresh;
